import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Shield, Clock, Handshake, CheckCircle2, Home, Wrench, Building2, DollarSign, FileText, Heart, ArrowRight } from 'lucide-react';

const LandingPage = () => {
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.getElementById('hero');
      if (heroSection) {
        const heroBottom = heroSection.getBoundingClientRect().bottom;
        setShowStickyCTA(heroBottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToForm = () => {
    const formElement = document.getElementById('consultation-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <div className="landing-page">
      {/* Header with Background Image */}
      <header className="header-banner">
        <div className="header-overlay"></div>
        <div className="header-content">
          <img 
            src="https://customer-assets.emergentagent.com/job_clear-options/artifacts/0034cghm_restoringhope%20bg%20removed%20logo.png" 
            alt="Prosperity Properties" 
            className="header-logo"
          />
        </div>
      </header>

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="container hero-content">
          <h1 className="hero-headline">
            Free Property Consultation — Clear Options, No Pressure
          </h1>
          <p className="hero-subheadline">
            If you're facing uncertainty with a property, Prosperity Properties offers a free, quick consultation to understand your situation, explore solutions, and help you move forward with clarity and confidence.
          </p>
          <Button onClick={scrollToForm} className="cta-button" size="lg">
            Request My Free Consultation
            <ArrowRight className="ml-2" size={20} />
          </Button>
          <div className="trust-indicators">
            <div className="trust-item">
              <Shield size={18} />
              <span>Confidential & respectful</span>
            </div>
            <div className="trust-item">
              <Clock size={18} />
              <span>No obligation</span>
            </div>
            <div className="trust-item">
              <Handshake size={18} />
              <span>Solutions-focused local investor</span>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Form Section */}
      <section id="consultation-form" className="form-section">
        <div className="container form-container">
          <div className="form-wrapper">
            <h2 className="form-title">Start With a Free Consultation</h2>
            <div className="google-form-container">
              <iframe 
                src="https://docs.google.com/forms/d/e/1FAIpQLSc-uDsw4qbl7cvIYdzmQ2ZakghFDmyhWHvDt7d29smbEDzOng/viewform?embedded=true" 
                width="100%" 
                height="900" 
                frameBorder="0" 
                marginHeight="0" 
                marginWidth="0"
                title="Property Consultation Request Form"
              >
                Loading…
              </iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Value Section */}
      <section className="value-section">
        <div className="container">
          <h2 className="section-title">How Prosperity Properties Helps</h2>
          <div className="value-grid">
            <div className="value-card">
              <div className="value-icon">
                <Handshake size={32} />
              </div>
              <h3>Solutions, not pressure</h3>
              <p>We listen first and provide options that work for your unique situation</p>
            </div>
            <div className="value-card">
              <div className="value-icon">
                <Home size={32} />
              </div>
              <h3>As-is options — no repairs required</h3>
              <p>Sell your property in its current condition without the hassle of fixes</p>
            </div>
            <div className="value-card">
              <div className="value-icon">
                <ArrowRight size={32} />
              </div>
              <h3>Flexible paths forward</h3>
              <p>Whether selling, partnering, or seeking guidance, we offer multiple routes</p>
            </div>
            <div className="value-card">
              <div className="value-icon">
                <Heart size={32} />
              </div>
              <h3>Respect-first approach</h3>
              <p>Every situation is handled with care, dignity, and understanding</p>
            </div>
          </div>
        </div>
      </section>

      {/* Common Situations Section */}
      <section className="situations-section">
        <div className="container">
          <h2 className="section-title">Common Situations We Help With</h2>
          <div className="situations-grid">
            <div className="situation-item">
              <FileText size={24} />
              <span>Inherited or probate properties</span>
            </div>
            <div className="situation-item">
              <Wrench size={24} />
              <span>Homes needing major repairs</span>
            </div>
            <div className="situation-item">
              <Building2 size={24} />
              <span>Vacant or problem properties</span>
            </div>
            <div className="situation-item">
              <DollarSign size={24} />
              <span>Financial pressure or time constraints</span>
            </div>
            <div className="situation-item">
              <CheckCircle2 size={24} />
              <span>Code or compliance concerns</span>
            </div>
            <div className="situation-item">
              <Heart size={24} />
              <span>Life transitions or uncertainty</span>
            </div>
          </div>
          <p className="situations-note">If your situation isn't listed, we're still happy to talk.</p>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="process-section">
        <div className="container">
          <h2 className="section-title">How It Works</h2>
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <h3>Submit your information</h3>
              <p>Fill out our simple form with your property details</p>
            </div>
            <div className="process-arrow">
              <ArrowRight size={32} />
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <h3>We review your property and situation</h3>
              <p>Our team carefully evaluates your unique circumstances</p>
            </div>
            <div className="process-arrow">
              <ArrowRight size={32} />
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <h3>You receive clear options and next steps</h3>
              <p>We present straightforward solutions tailored to your needs</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="container">
          <h2 className="section-title">About Prosperity Properties</h2>
          <p className="about-text">
            Prosperity Properties is a real estate investment company dedicated to helping homeowners find practical, respectful solutions during uncertain or challenging circumstances. We focus on understanding where you are, where you need to be, and how we can help bridge the gap — with transparency, care, and clarity.
          </p>
          <p className="tagline">
            <span className="tagline-purple">Restoring Homes.</span>{' '}
            <span className="tagline-gold">Restoring Hope.</span>{' '}
            <span className="tagline-purple">For Collaborative Community Impact.</span>
          </p>
        </div>
      </section>

      {/* Why Section */}
      <section className="why-section">
        <div className="container">
          <h2 className="section-title">Why Prosperity Properties Exists</h2>
          <p className="why-text">
            Prosperity Properties was created from personal experience. There was a time when the founder faced a property situation without clear guidance or understanding of available options. That experience shaped the mission of the company today — to help homeowners navigate uncertainty with clarity, respect, and real solutions.
          </p>
          <p className="tagline">
            <span className="tagline-purple">Restoring Homes.</span>{' '}
            <span className="tagline-gold">Restoring Hope.</span>{' '}
            <span className="tagline-purple">For Collaborative Community Impact.</span>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <img 
            src="https://customer-assets.emergentagent.com/job_clear-options/artifacts/0034cghm_restoringhope%20bg%20removed%20logo.png" 
            alt="Prosperity Properties" 
            className="footer-logo"
          />
          <p className="footer-text">We respect your privacy and do not sell your information.</p>
          <p className="footer-copyright">© {new Date().getFullYear()} Prosperity Properties. All rights reserved.</p>
        </div>
      </footer>

      {/* Sticky CTA for Mobile */}
      {showStickyCTA && (
        <div className="sticky-cta">
          <Button onClick={scrollToForm} className="sticky-cta-button" size="lg">
            Get Your Free Consultation
          </Button>
        </div>
      )}
    </div>
  );
};

export default LandingPage;